import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Cookies from "js-cookie";

import config from "../Config";
import useUser, { getAuthorized, isUserDoctor, patchAuthorized } from "../hooks/User";
import { getSituationSeverity } from "../constants/notificationClasses";

/** Интервал опроса API — согласован с интервалом воркера оценки (~30 с) */
export const NOTIFICATIONS_POLL_MS = 45_000;

const BANNER_DISMISS_KEY = "spiria-notification-banner-dismissed-ids";

function loadDismissedBannerIds() {
    try {
        const raw = localStorage.getItem(BANNER_DISMISS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function persistDismissedBannerIds(ids) {
    try {
        const truncated = ids.slice(-100);
        localStorage.setItem(BANNER_DISMISS_KEY, JSON.stringify(truncated));
    } catch {
        /* localStorage может быть недоступен */
    }
}

const defaultContext = {
    enabled: false,
    notifications: [],
    unreadTotal: 0,
    loading: false,
    error: null,
    newNotificationIds: [],
    dismissedBannerIds: [],
    refresh: () => {},
    markRead: async () => {},
    markAllRead: async () => {},
    dismissBanner: () => {},
    getBannerNotification: () => null,
};

const DoctorNotificationsContext = createContext(defaultContext);

function DoctorNotificationsProvider({ children }) {
    const { loading: userLoading, data: user } = useUser();
    const enabled =
        !userLoading &&
        isUserDoctor(user) &&
        Boolean(Cookies.get(config.authCookie));

    const [notifications, setNotifications] = useState([]);
    const [unreadTotal, setUnreadTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [newNotificationIds, setNewNotificationIds] = useState([]);
    const [dismissedBannerIds, setDismissedBannerIds] = useState(loadDismissedBannerIds);

    const initializedRef = useRef(false);
    const prevIdsRef = useRef(new Set());
    const fetchInFlightRef = useRef(false);

    const refresh = useCallback(async () => {
        if (!enabled || fetchInFlightRef.current) return;
        fetchInFlightRef.current = true;
        setLoading(true);
        setError(null);
        try {
            const { data } = await getAuthorized("/doctor/risk_alerts", {
                params: { is_read: false, limit: 50 },
            });
            const list = data?.alerts ?? [];
            const total = data?.total_count ?? list.length;
            setNotifications(list);
            setUnreadTotal(total);

            const currentIds = new Set(list.map((a) => a.id));
            if (!initializedRef.current) {
                initializedRef.current = true;
                prevIdsRef.current = currentIds;
            } else {
                const newly = [...currentIds].filter((id) => !prevIdsRef.current.has(id));
                if (newly.length > 0) {
                    setNewNotificationIds((prev) => [...new Set([...prev, ...newly])]);
                }
                prevIdsRef.current = currentIds;
            }
        } catch (err) {
            const message =
                err?.response?.data?.error_description ||
                err?.message ||
                "Не удалось загрузить уведомления";
            setError(message);
        } finally {
            setLoading(false);
            fetchInFlightRef.current = false;
        }
    }, [enabled]);

    useEffect(() => {
        if (!enabled) {
            setNotifications([]);
            setUnreadTotal(0);
            setNewNotificationIds([]);
            initializedRef.current = false;
            prevIdsRef.current = new Set();
            return;
        }

        refresh();
        const intervalId = setInterval(refresh, NOTIFICATIONS_POLL_MS);

        const onVisible = () => {
            if (document.visibilityState === "visible") refresh();
        };
        const onOnline = () => refresh();

        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("online", onOnline);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("online", onOnline);
        };
    }, [enabled, refresh]);

    const dismissBanner = useCallback((alertId) => {
        setDismissedBannerIds((prev) => {
            if (prev.includes(alertId)) return prev;
            const next = [...prev, alertId];
            persistDismissedBannerIds(next);
            return next;
        });
        setNewNotificationIds((prev) => prev.filter((id) => id !== alertId));
    }, []);

    const markRead = useCallback(
        async (alertId) => {
            try {
                await patchAuthorized(`/doctor/risk_alerts/${alertId}/read`, {});
                setNotifications((prev) => prev.filter((a) => a.id !== alertId));
                setUnreadTotal((prev) => Math.max(0, prev - 1));
                setNewNotificationIds((prev) => prev.filter((id) => id !== alertId));
                prevIdsRef.current.delete(alertId);
                return true;
            } catch (err) {
                if (err?.response?.status === 404) {
                    setNotifications((prev) => prev.filter((a) => a.id !== alertId));
                    setUnreadTotal((prev) => Math.max(0, prev - 1));
                }
                throw err;
            }
        },
        []
    );

    const markAllRead = useCallback(
        async () => {
            try {
                await patchAuthorized(`/doctor/risk_alerts/mark_all_read`, {});
                setNotifications([]);
                setUnreadTotal(0);
                setNewNotificationIds([]);
                prevIdsRef.current.clear();
                return true;
            } catch (err) {
                throw err;
            }
        },
        []
    );

    /** Самое приоритетное непрочитанное уведомление для верхнего баннера */
    const getBannerNotification = useCallback(() => {
        const candidates = notifications.filter(
            (a) =>
                newNotificationIds.includes(a.id) &&
                !dismissedBannerIds.includes(a.id)
        );
        if (candidates.length === 0) return null;
        return [...candidates].sort(
            (a, b) => getSituationSeverity(b.situation_class) - getSituationSeverity(a.situation_class)
        )[0];
    }, [notifications, newNotificationIds, dismissedBannerIds]);

    const value = useMemo(
        () => ({
            enabled,
            notifications,
            unreadTotal,
            loading,
            error,
            newNotificationIds,
            dismissedBannerIds,
            refresh,
            markRead,
            markAllRead,
            dismissBanner,
            getBannerNotification,
        }),
        [
            enabled,
            notifications,
            unreadTotal,
            loading,
            error,
            newNotificationIds,
            dismissedBannerIds,
            refresh,
            markRead,
            markAllRead,
            dismissBanner,
            getBannerNotification,
        ]
    );

    return (
        <DoctorNotificationsContext.Provider value={value}>
            {children}
        </DoctorNotificationsContext.Provider>
    );
}

function useDoctorNotificationsContext() {
    return useContext(DoctorNotificationsContext);
}

export {
    DoctorNotificationsContext,
    DoctorNotificationsProvider,
    useDoctorNotificationsContext,
    BANNER_DISMISS_KEY,
};
